import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { CalendarCheck, ArrowLeft, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const ForgotPasswordPage = () => {
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [email, setEmail] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email });
      setSubmitted(true);
      toast.success('Check your email for reset instructions');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-paper text-ink flex flex-col">
      <header className="border-b border-ink-200 bg-paper/90 backdrop-blur sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5" data-testid="forgot-logo-link">
            <div className="w-7 h-7 bg-ink flex items-center justify-center">
              <CalendarCheck className="w-4 h-4 text-paper" />
            </div>
            <span className="font-display font-bold text-lg tracking-tight">Enota</span>
          </Link>
          <Link to="/login" className="text-sm text-ink-600 hover:text-ink flex items-center gap-1.5" data-testid="forgot-back-login">
            <ArrowLeft className="w-4 h-4" /> Back to login
          </Link>
        </div>
      </header>

      <main className="flex-1 flex items-center justify-center p-4 grid-bg">
        <div className="w-full max-w-md">
          <div className="card-base p-8">
            <p className="eyebrow mb-3">PASSWORD RESET</p>
            <h1 className="h2 mb-1">{submitted ? 'Check your email.' : 'Forgot your password?'}</h1>
            <p className="text-ink-600 text-sm mb-6">
              {submitted ? "If the account exists, a reset link is on its way." : "Drop in your email and we'll send a fresh reset link."}
            </p>

            {submitted ? (
              <div className="text-center py-2">
                <div className="w-14 h-14 border border-success/40 bg-success/10 flex items-center justify-center mx-auto mb-5">
                  <CheckCircle2 className="w-7 h-7 text-success" />
                </div>
                <p className="text-ink-600 text-sm mb-5">
                  If an account exists for <strong className="text-ink">{email}</strong>, a reset link is on its way. Check spam too.
                </p>
                <button onClick={() => setSubmitted(false)} className="btn btn-outline w-full" data-testid="try-again-btn">
                  Try another email
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label htmlFor="email" className="eyebrow mb-2 block">Email address</label>
                  <input
                    id="email" type="email" placeholder="name@example.com" className="input-editorial"
                    value={email} onChange={(e) => setEmail(e.target.value)} required
                    data-testid="forgot-email-input"
                  />
                </div>
                <button type="submit" className="btn btn-primary w-full" disabled={loading} data-testid="forgot-submit-btn">
                  {loading ? 'Sending…' : 'Send reset link'}
                </button>
              </form>
            )}

            <p className="text-center text-sm text-ink-600 mt-6">
              Remember your password?{' '}
              <Link to="/login" className="text-respondent font-medium hover:underline" data-testid="forgot-login-link">Sign in</Link>
            </p>
          </div>
        </div>
      </main>
    </div>
  );
};

export default ForgotPasswordPage;
